import { useCallback } from 'react';
import { storeInterestData, getStoredInterestData, InterestData } from '../utils/interestDataUtils';
import { useVehicleInterest } from './useVehicleInterest';
import { logger } from '../utils/logger';

/**
 * Centralized hook for managing all vehicle interest data
 * This replaces all fragmented vehicle storage mechanisms
 */
export const useCentralizedVehicleInterest = () => {
  const { insertVehicleInterest } = useVehicleInterest();

  /**
   * Store vehicle interest data with comprehensive vehicle information
   * This is the SINGLE entry point for storing vehicle data
   */
  const storeVehicleInterest = useCallback(async (vehicleData: any, options?: {
    sessionId?: string;
    dealerId?: number;
    source?: 'Site' | 'Chat' | 'AI';
    siteUrl?: string;
    message?: string;
    summary?: string;
  }) => {
    try {
      logger.log('🚗 CENTRALIZED: Storing vehicle interest data:', vehicleData);

      // Normalize vehicle data to match InterestData interface with comprehensive mapping
      const normalizedVehicle: InterestData['vehicle'] = {
        make: vehicleData.Make || vehicleData.make || '',
        model: vehicleData.Model || vehicleData.model || '',
        version: vehicleData.Version || vehicleData.version || vehicleData.Trim || vehicleData.trim || '',
        trim: vehicleData.Trim || vehicleData.trim || vehicleData.Version || vehicleData.version || '',
        year: vehicleData.Year || vehicleData.year || '',
        color: vehicleData.Color || vehicleData['Exterior Color'] || vehicleData.Exterior_Color || vehicleData.exterior_color || vehicleData.color || '',
        vin: vehicleData.VIN || vehicleData.vin || '',
        VIN: vehicleData.VIN || vehicleData.vin || '', // Keep both for compatibility
        stock: vehicleData.Stock_Number || vehicleData.stock || '',
        main_picture: vehicleData.Main_Picture_URL || vehicleData.Main_Picture || vehicleData.main_picture || '',
        carfax_url: vehicleData.CARFAX_URL || vehicleData.carfax_url || '',
        status: vehicleData.Status || vehicleData.status || '',
        price: vehicleData.Price || vehicleData.price || undefined,
        mileage: vehicleData.Mileage || vehicleData.mileage || undefined,
        vdp_url: vehicleData.VDP_URL || vehicleData.vdp_url || '',
        exterior_color: vehicleData.Color || vehicleData['Exterior Color'] || vehicleData.Exterior_Color || vehicleData.exterior_color || '',
        interior_color: vehicleData['Interior Color'] || vehicleData.Interior_Color || vehicleData.interior_color || '',
        engine: vehicleData.Engine || vehicleData.engine || '',
        transmission: vehicleData.Transmission || vehicleData.transmission || '',
        fuel_type: vehicleData.Fuel_Type || vehicleData.fuel_type || '',
        drivetrain: vehicleData.Drivetrain || vehicleData.drivetrain || '',
        body_style: vehicleData.Body_Style || vehicleData.body_style || '',
        features: vehicleData.Features || vehicleData.features || [],
        description: vehicleData.Vehicle_Description_Summary || vehicleData.description || ''
      };

      logger.log('🚗 CENTRALIZED: Normalized vehicle data:', JSON.stringify(normalizedVehicle, null, 2));

      // Determine department based on vehicle mileage/year for routing (CRITICAL FOR ADF STATUS)
      let determinedDepartment = 'New'; // default
      
      if (normalizedVehicle.mileage !== undefined && normalizedVehicle.mileage !== null) {
        const mileage = typeof normalizedVehicle.mileage === 'string' ? 
          parseFloat(normalizedVehicle.mileage.replace(/[^\d.]/g, '')) : normalizedVehicle.mileage;
        
        if (mileage > 200) {
          determinedDepartment = 'Used';
          logger.log(`🎯 CENTRALIZED: Vehicle classified as USED based on mileage: ${mileage}km`);
        }
      } else if (normalizedVehicle.year) {
        const currentYear = new Date().getFullYear();
        const vehicleYear = parseInt(normalizedVehicle.year.toString());
        
        if (vehicleYear < currentYear) {
          determinedDepartment = 'Used';
          logger.log(`🎯 CENTRALIZED: Vehicle classified as USED based on year: ${vehicleYear}`);
        }
      }

      // Create comprehensive interest data
      const interestData: InterestData = {
        vehicle: normalizedVehicle,
        message: options?.message || '',
        summary: options?.summary || '',
        department: determinedDepartment, // CRITICAL: Set department based on vehicle analysis
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_term: '',
        utm_content: ''
      };

      // Store in centralized storage
      await storeInterestData(interestData);

      // Also track in database if session info provided
      if (options?.sessionId && options?.dealerId) {
        const vehicleDescription = `${normalizedVehicle.year || ''} ${normalizedVehicle.make || ''} ${normalizedVehicle.model || ''}`.trim();
        if (vehicleDescription) {
          await insertVehicleInterest(
            options.sessionId,
            options.dealerId,
            vehicleDescription,
            options.source || 'Chat',
            options.siteUrl
          );
        }
      }

      logger.log('🚗 CENTRALIZED: Vehicle interest stored successfully');
      return true;
    } catch (error) {
      logger.error('🚗 CENTRALIZED: Error storing vehicle interest:', error);
      return false;
    }
  }, [insertVehicleInterest]);

  /**
   * Get stored vehicle interest data
   */
  const getVehicleInterest = useCallback(async () => {
    try {
      const storedData = await getStoredInterestData();
      return storedData?.vehicle || null;
    } catch (error) {
      logger.error('🚗 CENTRALIZED: Error getting vehicle interest:', error);
      return null;
    }
  }, []);

  /**
   * Get complete stored interest data (vehicle + other data)
   */
  const getCompleteInterestData = useCallback(async () => {
    try {
      return await getStoredInterestData();
    } catch (error) {
      logger.error('🚗 CENTRALIZED: Error getting complete interest data:', error);
      return null;
    }
  }, []);

  return {
    storeVehicleInterest,
    getVehicleInterest,
    getCompleteInterestData
  };
};